import React, { useEffect, useState, useCallback } from 'react'
import {
  Table, Button, Input, Select, Tag, Space, Modal, Drawer,
  Typography, Form, message, Descriptions, Divider,
} from 'antd'
import { PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons'
import { documentApi } from '../../services/api'
import useAuthStore from '../../store/authStore'

const { Title, Paragraph, Text } = Typography
const { TextArea } = Input

const CATEGORIES = ['产品手册', '配置指南', '故障排查', '方案模板', '行业标准']
const CATEGORY_COLORS = {
  '产品手册': 'blue',
  '配置指南': 'green',
  '故障排查': 'red',
  '方案模板': 'orange',
  '行业标准': 'purple',
}

export default function Documents() {
  const [data, setData] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [query, setQuery] = useState({ page: 1, pageSize: 20, keyword: '', category: '' })
  const user = useAuthStore((s) => s.user)

  // Modal
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)

  // Drawer
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [detail, setDetail] = useState(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const res = await documentApi.list(query)
      setData(res.data)
      setTotal(res.total)
    } catch (e) {
      message.error('加载文档列表失败: ' + (e?.error || e?.message || ''))
    } finally {
      setLoading(false)
    }
  }, [query])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      setSaving(true)
      if (editing) {
        await documentApi.update(editing.id, values)
        message.success('文档更新成功')
      } else {
        await documentApi.create(values)
        message.success('文档创建成功')
      }
      setModalOpen(false)
      setEditing(null)
      form.resetFields()
      fetchData()
    } catch (e) {
      if (e?.errorFields) return // 表单验证错误
      message.error('操作失败: ' + (e?.error || e?.message || ''))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (record) => {
    if (user?.role !== 'admin') {
      message.warning('仅管理员可删除文档')
      return
    }
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除文档"${record.title}"吗？`,
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await documentApi.delete(record.id)
          message.success('文档已删除')
          fetchData()
        } catch (e) {
          message.error('删除失败: ' + (e?.error || e?.message || ''))
        }
      },
    })
  }

  const handleView = async (record) => {
    try {
      const res = await documentApi.get(record.id)
      setDetail(res)
      setDrawerOpen(true)
    } catch (e) {
      message.error('加载文档详情失败')
    }
  }

  const handleEdit = (record) => {
    setEditing(record)
    form.setFieldsValue({
      title: record.title,
      category: record.category,
      content: record.content,
      tags: record.tags,
      fileUrl: record.fileUrl,
      fileType: record.fileType,
    })
    setModalOpen(true)
  }

  const columns = [
    {
      title: '标题', dataIndex: 'title', key: 'title',
      ellipsis: true,
      render: (v) => <Text strong>{v}</Text>,
    },
    {
      title: '分类', dataIndex: 'category', key: 'category', width: 120,
      render: (v) => <Tag color={CATEGORY_COLORS[v] || 'default'}>{v}</Tag>,
    },
    {
      title: '标签', dataIndex: 'tags', key: 'tags', width: 180,
      render: (v) => v ? v.split(',').map((t) => <Tag key={t} style={{ marginBottom: 2 }}>{t.trim()}</Tag>) : '—',
    },
    {
      title: '上传者', key: 'user', width: 100,
      render: (_, r) => r.user?.name || '—',
    },
    {
      title: '更新时间', dataIndex: 'updatedAt', key: 'updatedAt', width: 160,
      render: (v) => v ? new Date(v).toLocaleString('zh-CN') : '—',
    },
    {
      title: '操作', key: 'actions', width: 200,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => handleView(r)}>查看</Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(r)}>编辑</Button>
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(r)}>删除</Button>
        </Space>
      ),
    },
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>技术文档</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => {
          setEditing(null)
          form.resetFields()
          setModalOpen(true)
        }}>上传文档</Button>
      </div>

      <Space style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="搜索标题/内容/标签"
          allowClear
          style={{ width: 260 }}
          prefix={<SearchOutlined />}
          onSearch={(v) => setQuery((q) => ({ ...q, keyword: v, page: 1 }))}
        />
        <Select
          placeholder="筛选分类"
          allowClear
          style={{ width: 140 }}
          onChange={(v) => setQuery((q) => ({ ...q, category: v || '', page: 1 }))}
        >
          {CATEGORIES.map((c) => (
            <Select.Option key={c} value={c}>{c}</Select.Option>
          ))}
        </Select>
      </Space>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          total,
          current: query.page,
          pageSize: query.pageSize,
          showTotal: (t) => `共 ${t} 篇文档`,
          onChange: (page) => setQuery((q) => ({ ...q, page })),
        }}
      />

      {/* 上传/编辑 Modal */}
      <Modal
        title={editing ? '编辑文档' : '上传文档'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => { setModalOpen(false); setEditing(null); form.resetFields() }}
        confirmLoading={saving}
        width={720}
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入标题' }]}>
            <Input placeholder="文档标题" />
          </Form.Item>
          <Form.Item name="category" label="分类" rules={[{ required: true, message: '请选择分类' }]}>
            <Select placeholder="选择分类">
              {CATEGORIES.map((c) => (
                <Select.Option key={c} value={c}>{c}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="content" label="内容" rules={[{ required: true, message: '请输入文档内容' }]}>
            <TextArea rows={12} placeholder="支持 Markdown 格式" />
          </Form.Item>
          <Form.Item name="tags" label="标签">
            <Input placeholder="多个标签用逗号分隔，如：华为,交换机,配置" />
          </Form.Item>
          <Space>
            <Form.Item name="fileUrl" label="文件链接" style={{ marginBottom: 0 }}>
              <Input placeholder="附件链接（可选）" style={{ width: 280 }} />
            </Form.Item>
            <Form.Item name="fileType" label="文件类型" style={{ marginBottom: 0 }}>
              <Select placeholder="类型" allowClear style={{ width: 120 }}>
                <Select.Option value="pdf">PDF</Select.Option>
                <Select.Option value="docx">Word</Select.Option>
                <Select.Option value="md">Markdown</Select.Option>
                <Select.Option value="txt">文本</Select.Option>
              </Select>
            </Form.Item>
          </Space>
        </Form>
      </Modal>

      {/* 详情 Drawer */}
      <Drawer
        title={detail?.title}
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setDetail(null) }}
        width={640}
      >
        {detail && (
          <div>
            <Descriptions column={2} size="small" bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="分类">
                <Tag color={CATEGORY_COLORS[detail.category]}>{detail.category}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="版本">v{detail.version}</Descriptions.Item>
              <Descriptions.Item label="上传者">{detail.user?.name}</Descriptions.Item>
              <Descriptions.Item label="文件类型">{detail.fileType || '—'}</Descriptions.Item>
              <Descriptions.Item label="创建时间">{new Date(detail.createdAt).toLocaleString('zh-CN')}</Descriptions.Item>
              <Descriptions.Item label="更新时间">{new Date(detail.updatedAt).toLocaleString('zh-CN')}</Descriptions.Item>
              <Descriptions.Item label="标签" span={2}>
                {detail.tags ? detail.tags.split(',').map((t) => <Tag key={t}>{t.trim()}</Tag>) : '—'}
              </Descriptions.Item>
              {detail.product && (
                <Descriptions.Item label="关联产品" span={2}>
                  <Tag color="blue">{detail.product.name}</Tag>
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider orientation="left">文档内容</Divider>
            <Paragraph style={{
              whiteSpace: 'pre-wrap',
              background: '#fafafa',
              padding: 16,
              borderRadius: 8,
              maxHeight: 400,
              overflow: 'auto',
            }}>
              {detail.content}
            </Paragraph>

            {detail.chunks?.length > 0 && (
              <>
                <Divider orientation="left">文档分段（共 {detail.chunks.length} 段）</Divider>
                {detail.chunks.map((chunk) => (
                  <div key={chunk.id} style={{
                    background: '#f5f5f5',
                    padding: '8px 12px',
                    borderRadius: 6,
                    marginBottom: 8,
                    fontSize: 12,
                  }}>
                    <Text type="secondary">段落 {chunk.chunkIndex + 1}: </Text>
                    <Text>{chunk.content.substring(0, 100)}{chunk.content.length > 100 ? '...' : ''}</Text>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </Drawer>
    </div>
  )
}
